import React, { createContext, useCallback, useEffect } from 'react';
import useLocalStorage from 'use-local-storage';
import { useOneButtonStore } from '../store/oneButtonStore';

interface ThemeContextType {
    theme: string;
    setTheme: (theme: string) => void;
    toggleTheme: () => void;
}

function readInitialTheme(defaultLight: boolean): string {
    if (typeof window === 'undefined') {
        return defaultLight ? 'light' : 'dark';
    }
    try {
        const t = localStorage.getItem('theme');
        if (t === 'light' || t === 'dark') return t;
        const legacy = localStorage.getItem('obd-dashboard-theme');
        if (legacy === 'light' || legacy === 'dark') return legacy;
    } catch {
        /* ignore */
    }
    return defaultLight ? 'light' : 'dark';
}

const ThemeContext = createContext<ThemeContextType>({
    theme: 'light',
    setTheme: () => {},
    toggleTheme: () => {},
});

const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
    const defaultLight = window.matchMedia('(prefers-color-scheme: light)').matches;
    const [theme, setThemeStorage] = useLocalStorage('theme', readInitialTheme(defaultLight));

    const setTheme = useCallback(
        (next: string) => {
            if (next === 'light' || next === 'dark') {
                setThemeStorage(next);
            }
        },
        [setThemeStorage],
    );

    const toggleTheme = useCallback(() => {
        setThemeStorage(theme === 'dark' ? 'light' : 'dark');
    }, [theme, setThemeStorage]);

    useEffect(() => {
        if (theme === 'light') {
            document.body.classList.remove('dark-theme');
            document.body.classList.add('light-theme');
        } else if (theme === 'dark') {
            document.body.classList.remove('light-theme');
            document.body.classList.add('dark-theme');
        }
    }, [theme]);

    useEffect(() => {
        const t = theme === 'light' ? 'light' : 'dark';
        useOneButtonStore.getState().setTheme(t);
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export { ThemeProvider, ThemeContext };
